"use client"

// Segment error boundary (25 Sep 2026, BOO 3840c13 parity). A network drop mid-checkout (a failed server action or
// RSC fetch) used to replace the whole page with Next's blank "Application error" screen. This keeps the segment's
// layout (header, footer / checkout header) and the cart (it lives on the server, keyed by the cart cookie), says
// what happened and offers a Retry that re-fetches the page from the server.

import { useRouter } from "next/navigation"
import { useEffect, useTransition } from "react"

export default function SegmentError({
  error,
  reset,
  where,
}: {
  error: Error & { digest?: string }
  reset: () => void
  where: string
}) {
  const router = useRouter()
  const [pending, startTransition] = useTransition()

  useEffect(() => {
    // Boundaries swallow the error, so the client error reporter never sees it: send it on explicitly.
    try {
      const body = JSON.stringify({
        kind: "boundary:" + where,
        path: location.pathname,
        message: String(error?.message || "error").slice(0, 400),
        stack: error?.digest ? `digest ${error.digest}` : String(error?.stack || "").slice(0, 1200),
      })
      if (!navigator.sendBeacon?.("/api/client-error", new Blob([body], { type: "application/json" })))
        fetch("/api/client-error", { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true }).catch(() => {})
    } catch {}
  }, [error, where])

  const retry = () =>
    startTransition(() => {
      router.refresh()
      reset()
    })

  return (
    <div className="content-container py-16 flex flex-col items-center text-center" role="alert" data-testid="segment-error">
      <h1 className="font-heading text-3xl">Something went wrong — please try again</h1>
      <p className="text-elevate-grey-mid mt-3 max-w-md text-sm">
        We couldn&apos;t load this page, possibly because the connection dropped. Your cart is saved, so nothing is lost.
      </p>
      <button
        type="button"
        onClick={retry}
        disabled={pending}
        className="mt-6 bg-elevate-black text-white px-6 py-3 text-xs uppercase tracking-[0.1em] hover:bg-elevate-gold-dark disabled:opacity-60"
        data-testid="segment-error-retry"
      >
        {pending ? "Retrying…" : "Retry"}
      </button>
    </div>
  )
}
